import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import { Tags } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Tags',
    description: 'Manage tags',
    i18nKey: 'tags',
    permissionModuleKey: 'tags',
    route: {
      list: '/tags',
      new: '/tags/new',
      detail: (id: number) => `/tags/${id}`,
      edit: (id: number) => `/tags/${id}/edit`,
    },
    api: {
      list: '/tags',
      detail: (id: number) => `/tags/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search tags...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['name'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'Type to filter name...',
        type: 'text' as const,
        getValue: (item: Tags) => item.name,
        options: [],
      },
    ],
    applyFilter: (items: Tags[], filters: { name: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.name;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.name || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.name || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Tag',
        showTitle: false,
        variant: 'primary' as const,
        href: '/tags/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Tags) => {
      window.location.href = `/tags/${item.id}`;
    },
    keyExtractor: (item: Tags) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new tag.',
    noSearchResults: 'No tags found matching your search criteria.',
    noFilterResults: 'No tags match the selected filters.',
  },
  delete: {
    title: 'Delete Tag',
    message: 'Are you sure you want to delete this tag? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete tag. Please try again.',
    handler: async (id: number) => {
      const { deleteTag } = await import('../../../services/mutations');
      await deleteTag(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Tag Details',
    notFoundTitle: 'Tag not found',
    notFoundMessage: 'The tag you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Tags',
    defaultTitle: 'Unnamed Tag',
    getTitle: (item: Tags) => {
      return item.name || `Tag #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Tag',
      subtitle: 'Add a new tag',
      submitText: 'Create Tag',
    },
    edit: {
      title: 'Edit Tag',
      subtitle: 'Update tag information',
      submitText: 'Update Tag',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading tag...',
    errorMessage: 'Failed to save tag. Please try again.',
    create: async (data: Partial<Tags>) => {
      const { createTag } = await import('../../../services/mutations');
      return await createTag(data);
    },
    update: async (id: number, data: Partial<Tags>) => {
      const { updateTag } = await import('../../../services/mutations');
      return await updateTag(id, data);
    },
  },
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter tag name',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter tag description (optional)',
      rows: 3,
    },
    {
      name: 'color',
      label: 'Color',
      type: 'text',
      placeholder: 'Enter color code (e.g., #FF0000)',
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Tag Information',
        icon: '📝',
        fields: ['name', 'description', 'color'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<Tags>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (value, row) => (
      <div className="flex items-center space-x-2 flex-nowrap">
        {row.color && (
          <span 
            className="w-4 h-4 rounded-full inline-block"
            style={{ backgroundColor: row.color }}
          />
        )}
        <span className="text-sm text-gray-900">{value || '-'}</span>
      </div>
    ),
  },
  {
    key: 'description',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: Tags,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Name',
          value: item.name || '-',
        },
        {
          label: 'Description',
          value: item.description || '-',
        },
        {
          label: 'Color',
          value: item.color ? (
            <div className="flex items-center space-x-2 flex-nowrap">
              <span 
                className="w-6 h-6 rounded-full inline-block border border-gray-300"
                style={{ backgroundColor: item.color }}
              />
              <span>{item.color}</span>
            </div>
          ) : '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: Tags) => {
  return null;
};

export const getInitialFormData = (): Partial<Tags> => {
  return {
    name: '',
    description: '',
    color: '',
  };
};

export const mapItemToFormData = (item: Tags): Partial<Tags> => {
  return {
    name: item.name || '',
    description: item.description || '',
    color: item.color || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: Tags,
  onDelete: (id: number) => void,
  _a?: unknown,
  _b?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <div className="flex items-center space-x-2 flex-nowrap">
      <Link 
        to={moduleConfig.module.route.detail(item.id)}
        className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="View"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </Link>
      {canEdit && (
        <Link 
          to={moduleConfig.module.route.edit(item.id)}
          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </Link>
      )}
      {canDelete && (
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};
